import { Bike, Clock, ShoppingBag, Utensils } from 'lucide-react';
import type { PedidoEnBandejaVista } from '@shared/types';
import { accionDeTarjeta, type AccionBandeja } from '@shared/accionBandeja';
import { formatPrecio } from '@renderer/utils/formatters';

/**
 * Sprint 123 — Una tarjeta de la bandeja: quién es, cuánto lleva esperando, y UNA cosa que hacer.
 *
 * El diseño no es nuevo: es el de las tarjetas de `A domicilio` —*qué espera, desde hace cuánto, y
 * una acción*— aplicado a los tres canales. Se eligió ése y no el de una tabla porque es el que la
 * dueña ya usa todos los días.
 *
 * **El ícono y el color dicen el canal sin leer nada.** Para quien atiende con el cliente enfrente,
 * distinguir 🍽️ / 🛍️ / 🛵 de un vistazo vale más que cualquier etiqueta.
 */

const ICONO: Record<PedidoEnBandejaVista['canal'], { fondo: string; icono: JSX.Element }> = {
  mesa: { fondo: 'bg-emerald-500', icono: <Utensils className="h-4 w-4" /> },
  mostrador: { fondo: 'bg-teal-600', icono: <ShoppingBag className="h-4 w-4" /> },
  domicilio: { fondo: 'bg-orange-500', icono: <Bike className="h-4 w-4" /> },
};

/**
 * A quién se le entrega. Un folio sin nombre obliga a gritar un número y nadie levanta la vista
 * (Sprint 111); en mesa el "quién" es la mesa.
 */
function aQuien(p: PedidoEnBandejaVista): string {
  if (p.canal === 'mesa') return p.mesaNumero ? `Mesa ${p.mesaNumero}` : 'Mesa';
  return p.clienteNombre?.trim() || 'Sin nombre';
}

/** El detalle que explica por qué esta tarjeta está donde está. */
function detalle(p: PedidoEnBandejaVista): string | null {
  if (p.canal === 'mesa' && p.cuentaSolicitada) return 'pidió la cuenta';
  if (p.nombreRepartidor) return `va con ${p.nombreRepartidor}`;
  if (p.motivo === 'llego_del_enlace') return 'entró por el enlace · nadie lo ha revisado';
  if (p.motivo === 'falta_cotizar_envio') return 'falta preguntar cuánto cobra la moto';
  if (p.motivo === 'falta_comprobante') {
    return p.avisoPagoCliente ? 'dice que ya transfirió' : 'esperando su comprobante';
  }
  return null;
}

export function TarjetaBandeja({
  pedido,
  onAccion,
}: {
  pedido: PedidoEnBandejaVista;
  onAccion: (accion: AccionBandeja, pedido: PedidoEnBandejaVista) => void;
}) {
  const { accion, etiqueta } = accionDeTarjeta(pedido);
  const canal = ICONO[pedido.canal];
  const det = detalle(pedido);

  return (
    <article
      className={`rounded-xl border p-3 ${
        pedido.esperaVencida ? 'border-rose-300 bg-rose-50' : 'border-slate-200 bg-white'
      }`}
    >
      <div className="flex items-start gap-2.5">
        <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-white ${canal.fondo}`}>
          {canal.icono}
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-mono text-sm font-bold">#{String(pedido.numeroPedido).padStart(5, '0')}</div>
          <div className="truncate text-sm text-slate-700">{aQuien(pedido)}</div>
          {det && (
            <div className={`truncate text-xs ${pedido.esperaVencida ? 'font-semibold text-rose-700' : 'text-slate-500'}`}>
              {det}
            </div>
          )}
        </div>
        <div className="shrink-0 text-right">
          <div className="font-mono text-sm font-bold">{formatPrecio(pedido.total, '$', 'es-MX')}</div>
          <div
            className={`flex items-center justify-end gap-1 text-xs ${
              pedido.esperaVencida ? 'font-bold text-rose-600' : 'text-slate-500'
            }`}
          >
            <Clock className="h-3 w-3" />
            {pedido.minutosEsperando} min
          </div>
        </div>
      </div>

      {/*
        Un solo botón, y de 44 px. La disciplina es del Sprint 111: *"el operador nunca elige entre
        dos botones, lee el único que hay"*. Cuál es se decide en `accionBandeja.ts` mirando el
        pedido entero — nunca la columna, porque en la misma columna conviven canales que necesitan
        llamadas distintas.
      */}
      <button
        onClick={() => onAccion(accion, pedido)}
        className="btn-primary mt-2.5 min-h-[44px] w-full justify-center text-sm"
      >
        {etiqueta}
      </button>
    </article>
  );
}
